import { useEffect, useRef, useState } from 'react'
import Modal from './Modal'
import Boton from './Boton'
import { npApi } from '../api/compras'
import { fmtFecha } from '../utils/fecha'
import { colors, text, space, font } from '../design/tokens'
import { mensajeError, hoverFila, st } from '../pages/emp900/_helpers'
import { Badge } from '../pages/emp900/_comun'
import { NP_ESTADO_LABEL, NP_ESTADO_TONO, fmtMoneda } from '../pages/compras/_np'

/**
 * "Nº de pedido + lupa", como en Presea: se tipea el número (completo
 * `NP 0001-00000012` o solo `12`) y Enter carga el pedido; la lupa (o F6)
 * abre la lista de los pedidos ELEGIBLES para la operación. `elegible(p)`
 * decide cuáles se pueden operar; `filtros` acota la consulta.
 */
export default function BuscadorPedido({ onCargar, elegible = () => true, filtros = {}, ayuda = '' }) {
  const [texto, setTexto] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [error, setError] = useState('')
  const [lista, setLista] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  const cargarPorId = async (id) => {
    const { data } = await npApi.obtener(id)
    onCargar(data.pedido)
  }

  const buscar = async () => {
    const q = texto.trim()
    if (!q) return
    setBuscando(true); setError('')
    try {
      const { data } = await npApi.listar({ q })
      const pedidos = data.pedidos || []
      const num = q.replace(/\D/g, '')
      const exacto = pedidos.find(p => p.numero === q)
        || pedidos.find(p => num && String(p.numero).replace(/\D/g, '').endsWith(num.padStart(8, '0')))
        || (pedidos.length === 1 ? pedidos[0] : null)
      if (!exacto) { setError(pedidos.length === 0 ? `No hay ningún pedido "${q}".` : 'Hay varios pedidos parecidos: usá la lupa para elegir.'); return }
      if (!elegible(exacto)) { setError(`El pedido ${exacto.numero} está ${NP_ESTADO_LABEL[exacto.estado] || exacto.estado}: no se puede operar acá.`); return }
      await cargarPorId(exacto.id)
      setTexto('')
    } catch (e) { setError(mensajeError(e)) } finally { setBuscando(false) }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }} onKeyDown={e => { if (e.key === 'F6') { e.preventDefault(); setLista(true) } }}>
        <span style={{ fontSize: text.body, color: colors.textSec, whiteSpace: 'nowrap' }}>Nº de pedido</span>
        <input ref={inputRef} style={{ ...st.input, maxWidth: 260, fontFamily: font.mono }} value={texto} placeholder="NP 0001-00000012 o 12" onChange={e => setTexto(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') buscar() }} />
        <Boton variante="secondary" onClick={buscar} disabled={buscando || !texto.trim()}>{buscando ? 'Buscando…' : 'Cargar'}</Boton>
        <Boton variante="secondary" onClick={() => setLista(true)} title="Elegir de la lista (F6)">🔍</Boton>
        {ayuda && <span style={{ fontSize: text.sec, color: colors.textMeta }}>{ayuda}</span>}
      </div>
      {error && <p style={{ ...st.info, color: colors.err, marginTop: space.sm }}>{error}</p>}
      {lista && <ListaPedidosModal filtros={filtros} elegible={elegible} onClose={() => setLista(false)} onElegir={async (p) => { setLista(false); setError(''); try { await cargarPorId(p.id) } catch (e) { setError(mensajeError(e)) } }} />}
    </div>
  )
}

function ListaPedidosModal({ filtros, elegible, onClose, onElegir }) {
  const [q, setQ] = useState('')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  useEffect(() => {
    let vivo = true
    setLoading(true)
    npApi.listar({ ...filtros, q: q || undefined }).then(({ data }) => { if (vivo) setRows((data.pedidos || []).filter(elegible)) }).catch(() => { if (vivo) setRows([]) }).finally(() => { if (vivo) setLoading(false) })
    return () => { vivo = false }
  }, [q, filtros, elegible])
  return (
    <Modal title="Elegir pedido" onClose={onClose} maxWidth="820px">
      <input style={{ ...st.input, marginBottom: space.md }} placeholder="Número o proveedor…" value={q} onChange={e => setQ(e.target.value)} autoFocus />
      <div style={st.tableWrap}>
        <table style={st.table}>
          <thead><tr>{['Número', 'Fecha', 'Proveedor', 'Saldo (u.)', 'Total', 'Estado'].map(h => <th key={h} style={{ ...st.th, ...(['Saldo (u.)', 'Total'].includes(h) ? { textAlign: 'right' } : {}) }}>{h}</th>)}</tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={6} style={st.empty}>Cargando…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={6} style={st.empty}>No hay pedidos sobre los que hacer esta operación.</td></tr>
                : rows.map(p => (
                  <tr key={p.id} style={st.trClick} {...hoverFila} onClick={() => onElegir(p)}>
                    <td style={st.tdMono}>{p.numero}</td>
                    <td style={st.tdFecha}>{fmtFecha(p.fecha)}</td>
                    <td style={st.td}>{p.proveedor_nombre}</td>
                    <td style={st.tdImporte}>{p.saldo_unidades != null ? Number(p.saldo_unidades).toLocaleString('es-AR') : '—'}</td>
                    <td style={st.tdImporte}>{fmtMoneda(p.total, p.moneda || 'ARS')}</td>
                    <td style={st.td}><Badge tono={NP_ESTADO_TONO[p.estado] || 'neutro'}>{NP_ESTADO_LABEL[p.estado] || p.estado}</Badge></td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </Modal>
  )
}
